import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType, OrderPriority } from '../../../types/admin';
import DashboardCard from '../components/DashboardCard';
import { PriorityBadge } from '../../work/components/PriorityBadge';
import { useAdminCompany } from '../../../shared/contexts/AdminCompanyContext';
import { OrderStatusPreviewPopover } from '../../orders/components/OrderStatusPreview';
import { formatReceivedDate } from '../../orders/lib/format-received-date';
import { useApi } from '../../../api/useApi';

// Concierge styling
const CONCIERGE_BORDER = '#7C3AED';

// Crown icon for concierge accounts
const CrownIcon: React.FC = () => (
  <svg className="w-4 h-4 mr-1 inline-block shrink-0" viewBox="0 0 24 24" fill="#7C3AED" data-testid="concierge-icon">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 2h14v2H5v-2z"/>
  </svg>
);

type PlatformRole = 'admin' | 'operator';

interface User {
  name?: string;
  email?: string;
}

interface AdminDashboardProps {
  brand: BrandConfig;
  Icons: IconsType;
  user?: User;
  onNavigate: (module: string, subSection?: string) => void;
  platformRole?: PlatformRole;
  onPlaceOrder?: () => void;
}

interface DispatchedOrder {
  id: string;
  orderNumber: string;
  account: string;
  receivedDate: string;
  type: string;
  orderType: string;
  accountType: 'concierge' | 'direct';
  priority: OrderPriority;
}

interface OnHoldOrder {
  id: string;
  orderNumber: string;
  account: string;
  status: string;
  holdReason: string | null;
  orderType: string;
  accountType: 'concierge' | 'direct';
  priority: OrderPriority;
}

interface OverdueOrder {
  orderNumber: string;
  priority: OrderPriority;
  account: string;
  daysOverdue: number;
  severity: 'critical' | 'warning';
  orderType: string;
  accountType: 'concierge' | 'direct';
  organizationId: string;
}

// Default reason sent with hold POST. The UI currently exposes a single
// "Hold" action; the backend requires one of the enum values.
const DEFAULT_HOLD_REASON = 'awaiting-court-response';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ brand, Icons, onNavigate, platformRole = 'admin', onPlaceOrder }) => {
  const { selectedCompanyId } = useAdminCompany();
  const { get, post } = useApi();

  const [dispatchedOrders, setDispatchedOrders] = useState<DispatchedOrder[]>([]);
  const [onHoldOrders, setOnHoldOrders] = useState<OnHoldOrder[]>([]);
  const [isLoadingDispatched, setIsLoadingDispatched] = useState(true);
  const [isLoadingOnHold, setIsLoadingOnHold] = useState(true);
  const [dispatchedError, setDispatchedError] = useState<string | null>(null);
  const [onHoldError, setOnHoldError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInFlightId, setActionInFlightId] = useState<string | null>(null);

  const fetchDispatched = useCallback(async () => {
    setIsLoadingDispatched(true);
    setDispatchedError(null);
    try {
      const data = await get<{ data: DispatchedOrder[] }>('/api/admin/orders/dispatched');
      setDispatchedOrders(data.data || []);
    } catch (err) {
      console.error('Failed to fetch dispatched orders:', err);
      setDispatchedError(err instanceof Error ? err.message : 'Failed to load dispatched orders');
    } finally {
      setIsLoadingDispatched(false);
    }
  }, [get]);

  const fetchOnHold = useCallback(async () => {
    setIsLoadingOnHold(true);
    setOnHoldError(null);
    try {
      const data = await get<{ data: OnHoldOrder[] }>('/api/admin/orders/on-hold');
      setOnHoldOrders(data.data || []);
    } catch (err) {
      console.error('Failed to fetch on-hold orders:', err);
      setOnHoldError(err instanceof Error ? err.message : 'Failed to load on-hold orders');
    } finally {
      setIsLoadingOnHold(false);
    }
  }, [get]);

  // Refetch whenever the impersonated company changes — useApi sends
  // X-Account-Id, so the server-side filter is keyed off this header.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchDispatched();
  }, [fetchDispatched, selectedCompanyId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchOnHold();
  }, [fetchOnHold, selectedCompanyId]);

  const handleHold = async (orderId: string) => {
    setActionInFlightId(orderId);
    setActionError(null);
    try {
      await post(`/api/admin/orders/${orderId}/hold`, { reason: DEFAULT_HOLD_REASON });
      await Promise.all([fetchDispatched(), fetchOnHold()]);
    } catch (err) {
      console.error('Failed to hold order:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to place order on hold');
    } finally {
      setActionInFlightId(null);
    }
  };

  const handleRelease = async (orderId: string) => {
    setActionInFlightId(orderId);
    setActionError(null);
    try {
      await post(`/api/admin/orders/${orderId}/release`);
      await Promise.all([fetchOnHold(), fetchDispatched()]);
    } catch (err) {
      console.error('Failed to release order:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to release order from hold');
    } finally {
      setActionInFlightId(null);
    }
  };

  // Overdue orders still use sample data — no backend endpoint exists yet.
  const overdueOrdersAll: OverdueOrder[] = [
    { orderNumber: 'ORD-2024-100', priority: 'urgent', account: 'ABC Law Partners', daysOverdue: 7, severity: 'critical', orderType: 'Court Filing', accountType: 'direct', organizationId: 'org-1' },
    { orderNumber: 'ORD-2024-101', priority: 'priority', account: 'Legal Services Inc', daysOverdue: 5, severity: 'critical', orderType: 'Records Retrieval', accountType: 'direct', organizationId: 'org-2' },
    { orderNumber: 'ORD-2024-102', priority: 'on-demand', account: 'Pacific Legal Group', daysOverdue: 3, severity: 'warning', orderType: 'Skip Tracing', accountType: 'concierge', organizationId: 'org-4' },
    { orderNumber: 'ORD-2024-103', priority: 'standard', account: 'Summit Attorneys', daysOverdue: 1, severity: 'warning', orderType: 'Courtesy Copy Delivery', accountType: 'direct', organizationId: 'org-5' }
  ];

  const overdueOrders = useMemo(() => {
    if (!selectedCompanyId) return overdueOrdersAll;
    return overdueOrdersAll.filter(o => o.organizationId === selectedCompanyId);
  }, [selectedCompanyId, overdueOrdersAll]);

  // Calculate stats based on filtered data
  const orderStats = useMemo(() => ({
    incoming: { count: selectedCompanyId ? 1 : 4, alerts: 0 },
    pending: { count: selectedCompanyId ? 2 : 7, alerts: selectedCompanyId ? 0 : 1 },
    executed: { count: selectedCompanyId ? 3 : 12, alerts: selectedCompanyId ? 0 : 1 },
    closed: { count: 0, alerts: 0 }
  }), [selectedCompanyId]);

  const getOverdueColor = (daysOverdue: number): string => {
    return daysOverdue > 5 ? '#DC2626' : '#D97706';
  };

  const renderSkeletonRows = (cols: string, rows: number = 3) => {
    const rowKeys = ['a', 'b', 'c', 'd', 'e', 'f'].slice(0, rows);
    const cellKeys = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7'];
    const cellCount = parseInt(cols.replace('grid-cols-', ''), 10) || 1;
    return (
      <>
        {rowKeys.map((rowKey, i) => (
          <div
            key={`skeleton-${rowKey}`}
            className={`grid ${cols} gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-sm items-center animate-pulse`}
            style={{
              borderTop: i > 0 ? `1px solid ${brand.colors.border}` : 'none',
              borderLeft: '4px solid transparent',
            }}
            data-testid="dashboard-table-skeleton-row"
          >
            {cellKeys.slice(0, cellCount).map((cellKey) => (
              <div key={cellKey} className="h-4 rounded bg-gray-200" />
            ))}
          </div>
        ))}
      </>
    );
  };

  const smallOutlineButtonStyle: React.CSSProperties = {
    borderColor: brand.colors.primary,
    color: brand.colors.link,
    backgroundColor: 'transparent',
  };

  return (
    <div className="flex-1 overflow-auto" style={{ backgroundColor: brand.colors.bgLight }}>
      {/* Activity Panels */}
      <div className="p-4 sm:p-6">
        {/* Page heading — the route's single top-level heading (R3), in the
            AdminHeader idiom (text-xl semibold + brand heading font). This
            page doesn't render AdminHeader — AdminLayout already owns the top
            bar (greeting h2 + CompanySelector + search) — so the idiom is
            adopted, not the component. */}
        <h1
          className="text-xl font-semibold mb-4"
          style={{
            color: brand.colors.secondary,
            fontFamily: brand.typography.fontFamily.heading
          }}
        >
          Dashboard
        </h1>
        {actionError && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm"
            role="alert"
            data-testid="dashboard-action-error"
          >
            {actionError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Orders to Dispatch */}
          <div
            className="rounded-xl shadow-xs p-4 sm:p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
            data-testid="orders-to-dispatch-panel"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h2 className="flex items-center gap-2 font-semibold" style={{ color: brand.colors.secondary }}>
                Orders to Dispatch
                <span
                  className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full"
                  style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary, minWidth: '1.5rem' }}
                >
                  {dispatchedOrders.length}
                </span>
              </h2>
              <a
                href="#"
                className="text-sm font-medium hover:underline min-h-touch sm:min-h-0 inline-flex items-center gap-1"
                style={{ color: brand.colors.link }}
                onClick={(e) => { e.preventDefault(); onNavigate('orders', 'dispatch'); }}
              >
                View All
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
            {dispatchedError && (
              <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert" data-testid="dispatched-error">
                {dispatchedError}
              </div>
            )}
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
              <div
                className="grid grid-cols-5 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-xs font-medium uppercase"
                style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
              >
                <span>Order #</span>
                <span>Account</span>
                <span>Received</span>
                <span>Order Type</span>
                <span>Actions</span>
              </div>
              {isLoadingDispatched ? (
                renderSkeletonRows('grid-cols-5')
              ) : (
                dispatchedOrders.map((order, index) => (
                  <div
                    key={order.id}
                    className="grid grid-cols-5 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-sm items-center"
                    style={{
                      borderTop: index > 0 ? `1px solid ${brand.colors.border}` : 'none',
                      borderLeft: order.accountType === 'concierge' ? `4px solid ${CONCIERGE_BORDER}` : '4px solid transparent',
                      color: brand.colors.text
                    }}
                  >
                    <span className="flex items-center gap-1" style={{ color: brand.colors.link }}>
                      {order.accountType === 'concierge' && <CrownIcon />}
                      {order.orderNumber}
                      <OrderStatusPreviewPopover orderId={order.orderNumber} brand={brand} />
                    </span>
                    <span>{order.account}</span>
                    <span>{formatReceivedDate(order.receivedDate, { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                    <div className="flex flex-col gap-1">
                      <span>{order.orderType}</span>
                      <PriorityBadge priority={order.priority} brand={brand} />
                    </div>
                    <span>
                      <button
                        type="button"
                        onClick={() => handleHold(order.id)}
                        disabled={actionInFlightId === order.id}
                        className="px-2 py-1 text-xs font-medium rounded border disabled:opacity-50"
                        style={smallOutlineButtonStyle}
                        data-testid={`hold-button-${order.id}`}
                      >
                        {actionInFlightId === order.id ? 'Holding…' : 'Hold'}
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Overdue Orders */}
          <div
            className="rounded-xl shadow-xs p-4 sm:p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
            data-testid="overdue-orders-panel"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h2 className="flex items-center gap-2 font-semibold" style={{ color: brand.colors.secondary }}>
                Overdue Orders
                <span
                  className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full"
                  style={{ backgroundColor: brand.colors.error || '#EF4444', color: 'white', minWidth: '1.5rem' }}
                >
                  {overdueOrders.length}
                </span>
              </h2>
              <a
                href="#"
                className="text-sm font-medium hover:underline min-h-touch sm:min-h-0 inline-flex items-center gap-1"
                style={{ color: brand.colors.link }}
                onClick={(e) => { e.preventDefault(); onNavigate('orders', 'overdue'); }}
              >
                View All
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
              <div
                className="grid grid-cols-4 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-xs font-medium uppercase"
                style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
              >
                <span>Order #</span>
                <span>Account</span>
                <span>Days Overdue</span>
                <span>Order Type</span>
              </div>
              {overdueOrders.map((order, index) => (
                <div
                  key={order.orderNumber}
                  className="grid grid-cols-4 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-sm items-center"
                  style={{
                    borderTop: index > 0 ? `1px solid ${brand.colors.border}` : 'none',
                    borderLeft: order.accountType === 'concierge' ? `4px solid ${CONCIERGE_BORDER}` : '4px solid transparent',
                    color: brand.colors.text
                  }}
                >
                  <span className="flex items-center gap-1" style={{ color: brand.colors.link }}>
                    {order.accountType === 'concierge' && <CrownIcon />}
                    {order.orderNumber}
                    <OrderStatusPreviewPopover orderId={order.orderNumber} brand={brand} />
                  </span>
                  <span>{order.account}</span>
                  <span
                    className="font-semibold"
                    style={{ color: getOverdueColor(order.daysOverdue) }}
                    data-testid={`overdue-severity-${order.severity}`}
                  >
                    {order.daysOverdue} {order.daysOverdue === 1 ? 'day' : 'days'}
                  </span>
                  <div className="flex flex-col gap-1">
                    <span>{order.orderType}</span>
                    <PriorityBadge priority={order.priority} brand={brand} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Orders on Hold Panel - Full Width Row */}
        <div className="mb-6 sm:mb-8">
          <div
            className="rounded-xl shadow-xs p-4 sm:p-5"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
            data-testid="orders-on-hold-panel"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h2 className="flex items-center gap-2 font-semibold" style={{ color: brand.colors.secondary }}>
                Orders on Hold
                <span
                  className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full"
                  style={{ backgroundColor: brand.colors.warning || '#F59E0B', color: 'white', minWidth: '1.5rem' }}
                >
                  {onHoldOrders.length}
                </span>
              </h2>
              <a
                href="#"
                className="text-sm font-medium hover:underline min-h-touch sm:min-h-0 inline-flex items-center gap-1"
                style={{ color: brand.colors.link }}
                onClick={(e) => { e.preventDefault(); onNavigate('orders', 'on-hold'); }}
              >
                View All
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
            {onHoldError && (
              <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert" data-testid="on-hold-error">
                {onHoldError}
              </div>
            )}
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
              <div
                className="grid grid-cols-6 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-xs font-medium uppercase"
                style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
              >
                <span>Order #</span>
                <span>Account</span>
                <span>Order Type</span>
                <span>Status</span>
                <span>Reason</span>
                <span>Actions</span>
              </div>
              {isLoadingOnHold ? (
                renderSkeletonRows('grid-cols-6')
              ) : (
                onHoldOrders.map((order, index) => (
                  <div
                    key={order.id}
                    className="grid grid-cols-6 gap-2 sm:gap-4 px-3 sm:px-4 py-2 text-sm items-center"
                    style={{
                      borderTop: index > 0 ? `1px solid ${brand.colors.border}` : 'none',
                      borderLeft: order.accountType === 'concierge' ? `4px solid ${CONCIERGE_BORDER}` : '4px solid transparent',
                      color: brand.colors.text
                    }}
                  >
                    <span className="flex items-center gap-1" style={{ color: brand.colors.link }}>
                      {order.accountType === 'concierge' && <CrownIcon />}
                      {order.orderNumber}
                      <OrderStatusPreviewPopover orderId={order.orderNumber} brand={brand} />
                    </span>
                    <span>{order.account}</span>
                    <div className="flex flex-col gap-1">
                      <span>{order.orderType}</span>
                      <PriorityBadge priority={order.priority} brand={brand} />
                    </div>
                    <span>{order.status}</span>
                    <span className="truncate">{order.holdReason ?? '—'}</span>
                    <span>
                      <button
                        type="button"
                        onClick={() => handleRelease(order.id)}
                        disabled={actionInFlightId === order.id}
                        className="px-2 py-1 text-xs font-medium rounded border disabled:opacity-50"
                        style={smallOutlineButtonStyle}
                        data-testid={`release-button-${order.id}`}
                      >
                        {actionInFlightId === order.id ? 'Releasing…' : 'Release'}
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Work Management Cards - New Section */}
        <div className="mb-6 sm:mb-8">
          <h2
            className="text-lg font-semibold mb-3 sm:mb-4"
            style={{ color: brand.colors.secondary }}
          >
            My Work
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <DashboardCard
              icon={Icons.Document}
              label="Queue"
              count={12}
              alerts={1}
              onClick={() => onNavigate('queue')}
              variant="secondary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Folder}
              label="My Active"
              count={3}
              onClick={() => onNavigate('my-work')}
              variant="primary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Check}
              label="Tasks"
              count={5}
              alerts={1}
              onClick={() => onNavigate('tasks')}
              variant="secondary"
              size="large"
              brand={brand}
            />
            {platformRole === 'admin' && (
              <DashboardCard
                icon={Icons.ProcessServing}
                label="Team"
                count={8}
                onClick={() => onNavigate('team')}
                variant="secondary"
                size="large"
                brand={brand}
              />
            )}
            {platformRole === 'admin' && (
              <DashboardCard
                icon={Icons.Chart}
                label="Performance"
                onClick={() => onNavigate('performance')}
                variant="secondary"
                size="large"
                brand={brand}
              />
            )}
          </div>
        </div>

        {/* Order Stats Cards */}
        <div className="mb-6 sm:mb-8">
          <h2
            className="text-lg font-semibold mb-3 sm:mb-4"
            style={{ color: brand.colors.secondary }}
          >
            Orders
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <DashboardCard
              icon={Icons.Document}
              label="Incoming"
              count={orderStats.incoming.count}
              onClick={() => onNavigate('orders', 'incoming')}
              variant="secondary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Clock}
              label="Pending"
              count={orderStats.pending.count}
              alerts={orderStats.pending.alerts}
              onClick={() => onNavigate('orders', 'pending')}
              variant="primary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Check}
              label="Executed"
              count={orderStats.executed.count}
              alerts={orderStats.executed.alerts}
              onClick={() => onNavigate('orders', 'executed')}
              variant="secondary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Folder}
              label="Closed"
              onClick={() => onNavigate('orders', 'closed')}
              variant="secondary"
              size="large"
              brand={brand}
            />
            <DashboardCard
              icon={Icons.Plus}
              label="Place Order"
              onClick={() => onPlaceOrder?.()}
              variant="secondary"
              size="large"
              brand={brand}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
